import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Lightbulb, MessageCircle, Image as ImageIcon, Network, Loader2, X, FileText } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { ConceptMap } from "@/components/ConceptMap";
import { SubjectBadge } from "@/components/SubjectBadge";
import { ListenButton } from "@/components/ListenButton";

const Section = ({ icon: Icon, title, color, children, testId }) => (
  <section className="ink-box rise rounded-3xl bg-white p-6 sm:p-8" data-testid={testId}>
    <div className="mb-5 flex items-center gap-3">
      <span className={`ink-box-sm flex h-10 w-10 items-center justify-center rounded-xl ${color}`}><Icon className="h-5 w-5" /></span>
      <h2 className="font-display text-base font-bold uppercase tracking-wide md:text-lg">{title}</h2>
    </div>
    {children}
  </section>
);

const Pending = ({ status, label }) => (
  <div className="flex min-h-40 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-neutral-400 bg-[var(--paper)] p-6 text-center text-sm font-bold text-neutral-600">
    {status === "processing" ? <><Loader2 className="mb-2 h-6 w-6 animate-spin" /> {label} in preparazione…</> : `${label} non è ancora disponibile`}
  </div>
);

export default function TermDetail() {
  const { id } = useParams();
  const [term, setTerm] = useState(null);
  const [error, setError] = useState(false);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    setTerm(null);
    api.get(`/terms/${id}`).then((r) => setTerm(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) return <p className="py-24 text-center font-bold" data-testid="term-not-found">Parola non trovata. <Link to="/" className="underline">Torna al glossario</Link></p>;
  if (!term) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <main className="mx-auto max-w-4xl px-5 pb-24 pt-8 sm:px-8" data-testid="term-detail">
      <Link to="/" data-testid="back-to-glossary" className="inline-flex items-center gap-2 text-sm font-extrabold hover:underline">
        <ArrowLeft className="h-4 w-4" /> Torna al glossario
      </Link>

      <header className="rise mb-10 mt-6">
        <SubjectBadge subject={term.subject} testId="term-subject" />
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <h1 className="font-display text-4xl font-bold sm:text-5xl lg:text-6xl" data-testid="term-word">{term.word}</h1>
          <ListenButton key={term.id} termId={term.id} />
        </div>
      </header>

      <div className="space-y-8">
        <Section icon={Lightbulb} title="Definizione semplice" color="bg-[var(--sun)]" testId="definition-section">
          <p className="text-lg font-semibold leading-relaxed sm:text-xl" data-testid="term-definition">{term.definition}</p>
          {term.curiosity && (
            <p className="mt-5 rounded-2xl bg-[var(--paper)] p-4 text-sm" data-testid="term-curiosity"><span className="font-extrabold">Lo sapevi? </span>{term.curiosity}</p>
          )}
        </Section>

        <Section icon={MessageCircle} title="Frasi di esempio" color="bg-[var(--mint)]" testId="examples-section">
          <ul className="space-y-8">
            {term.examples.map((ex, i) => (
              <li key={i} className="flex items-center gap-3" style={{ marginLeft: i % 2 ? "2rem" : 0 }}>
                <div className="bubble flex-1 px-5 py-4 text-base font-semibold" data-testid={`example-${i}`}>{ex}</div>
                <ListenButton key={`${term.id}-${i}-${ex}`} termId={term.id} example={i} compact testId={`listen-example-${i}`} />
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={ImageIcon} title="Il fumetto" color="bg-[var(--coral)]" testId="comic-section">
          {term.comic_status === "ready" ? (
            <button onClick={() => setZoom(true)} className="block w-full overflow-hidden rounded-2xl border-2 border-[var(--ink)]" data-testid="comic-image-button">
              <img src={mediaUrl(term.comic_path)} alt={`Fumetto: ${term.word}`} className="w-full transition-transform duration-300 hover:scale-[1.02]" data-testid="comic-image" />
            </button>
          ) : <Pending status={term.comic_status} label="Il fumetto" />}
        </Section>

        <Section icon={Network} title="La mappa concettuale" color="bg-[var(--sky)] text-white" testId="map-section">
          {term.map_status === "ready" && term.concept_map ? (
            <>
              <ConceptMap map={term.concept_map} />
              <div className="mt-6 flex justify-end">
                <Link to={`/scheda-mappa/${term.id}`} data-testid="worksheet-link" className="ink-box-sm ink-press flex items-center gap-2 rounded-full bg-[var(--sun)] px-4 py-2 text-sm font-extrabold">
                  <FileText className="h-4 w-4" /> Scheda da completare
                </Link>
              </div>
            </>
          ) : <Pending status={term.map_status} label="La mappa" />}
        </Section>
      </div>

      {zoom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setZoom(false)} data-testid="comic-zoom-overlay">
          <button className="absolute right-4 top-4 rounded-full bg-white p-2" data-testid="comic-zoom-close"><X className="h-5 w-5" /></button>
          <img src={mediaUrl(term.comic_path)} alt="" className="max-h-full max-w-full rounded-xl" />
        </div>
      )}
    </main>
  );
}
